import { useState } from 'react'
import { Alert, Slider, Stack, Switch, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'

import { DataCard } from '@/components/ui'
import { HARDWARE_COM_AVATAR, escolherSom, escolherVolume, type Robo } from '@/api/robo'

/**
 * O som do robô (spec 2026-10-08-robo-som §4.3). Só o Robô II tem alto-falante, e por isso
 * o card não aparece para os outros — inclusive o modelo ainda não informado.
 */
export function SomDoRobo({ robo }: { robo: Robo }) {
  const qc = useQueryClient()
  const [ligado, setLigado] = useState(robo.som ?? true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState(false)
  const [volume, setVolume] = useState(robo.volume ?? 70)
  const [volumeSalvo, setVolumeSalvo] = useState(robo.volume ?? 70)
  const [erroVolume, setErroVolume] = useState(false)

  if (robo.hardware_model !== HARDWARE_COM_AVATAR) return null

  const trocar = async (novo: boolean) => {
    const anterior = ligado
    setErro(false)
    setLigado(novo)
    setSalvando(true)
    try {
      const salvo = await escolherSom(novo)
      setLigado(salvo.som)
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
    } catch {
      setLigado(anterior)
      setErro(true)
    } finally {
      setSalvando(false)
    }
  }

  const ajustarVolume = async (novo: number) => {
    if (novo === volumeSalvo) return
    const anterior = volumeSalvo
    setErroVolume(false)
    setVolume(novo)
    setSalvando(true)
    try {
      const salvo = await escolherVolume(novo)
      setVolume(salvo.volume)
      setVolumeSalvo(salvo.volume)
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
    } catch {
      setVolume(anterior)
      setErroVolume(true)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <DataCard title="Som">
      <Stack gap="sm">
        <Switch
          label="Som"
          checked={ligado}
          disabled={salvando}
          onChange={(e) => trocar(e.currentTarget.checked)}
        />
        <Stack gap={4}>
          <Text size="sm" fw={500} id="robo-volume-label">Volume</Text>
          <Slider
            aria-labelledby="robo-volume-label"
            thumbLabel="Volume"
            min={10}
            max={100}
            step={10}
            value={volume}
            disabled={!ligado || salvando}
            label={(v) => `${v}%`}
            marks={[10, 50, 100].map((v) => ({ value: v, label: `${v}%` }))}
            onChange={setVolume}
            onChangeEnd={ajustarVolume}
            mb="md"
          />
        </Stack>
        <Text size="sm" c="dimmed">
          Bipes quando o robô liga, quando chega alerta novo, quando tudo se resolve, a cada passo da
          configuração e depois de uma atualização. O volume vale para todos os bipes. A troca chega ao robô em até 30 segundos.
        </Text>
        {erro && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Não foi possível trocar o som agora. O robô continua como estava.
          </Alert>
        )}
        {erroVolume && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Não foi possível trocar o volume agora. O robô continua como estava.
          </Alert>
        )}
      </Stack>
    </DataCard>
  )
}
